import { UsuariosService } from '../../../core/services/usuarios.service';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../core/services/theme.service';
import { MatIcon } from "@angular/material/icon";
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatRippleModule } from '@angular/material/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';

import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { SolutionTokenService } from '../../../core/services/solution-token.service';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { WorkspaceAccessService } from '../../../core/services/workspace-access.service';
import { SwitchWorkspaceModal } from '../../../shared/switch-workspace-modal/switch-workspace-modal';
import { urlDeImagenDePerfil } from '../../../core/utils/imagen-perfil.util';
import { SolucionesService } from '../../../core/services/soluciones.service';
import { SwitchSolutionModal } from '../../../shared/switch-solution-modal/switch-solution-modal';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-navbar',
  imports: [
    MatIcon,
    MatButtonModule,
    MatToolbarModule,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    NgOptimizedImage,
    MatRippleModule
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  host: {
    '(window:resize)': 'onResize()',
    // El menú de perfil es un popup propio, no un overlay de Material: se cierra
    // a mano al hacer clic fuera o con Escape.
    '(document:click)': 'cerrarMenuPerfil()',
    '(document:keydown.escape)': 'cerrarMenuPerfil()'
  }
})
export class Navbar {
  public translate: TranslateService = inject(TranslateService);
  public theme: ThemeService = inject(ThemeService);
  public auth = inject(AuthService);

  /**
   * Si este workspace ofrece cambiar de solucion.
   *
   * La lista sale del entorno --`workspacesSinCambioDeSolucion`-- y se quita un
   * id de ahi para volver a mostrarlo, sin tocar este componente. Vacia por
   * defecto: sin configuracion se comporta como siempre.
   *
   * Esconde el enlace, no cierra la puerta: quien conozca la direccion de otra
   * solucion entra igual escribiendola. El control de verdad vive en Console.
   */
  protected muestraCambioDeSolucion = computed(() => {
    const excluidos = environment.workspacesSinCambioDeSolucion ?? [];
    return !excluidos.includes(this.auth.workspaceId());
  });

  protected availableLanguages = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'pt', label: 'Português' }
  ];

  private langChangeSignal = toSignal(this.translate.onLangChange, { initialValue: null });
  protected currentLang = computed<string>(() => {
    this.langChangeSignal();
    return this.translate.currentLang || localStorage.getItem('language') || 'es';
  });

  protected setLanguage(langCode: string) {
    this.translate.use(langCode);
    localStorage.setItem('language', langCode);
    this.cerrarMenuPerfil();
  }

  protected isMobile = signal<boolean>(window.innerWidth <= 768);
  protected menuOpen = signal<boolean>(false);

  /** Popup de perfil anclado sobre el pie del drawer, en rail y en expandido. */
  protected menuPerfilAbierto = signal<boolean>(false);
  protected submenuIdiomaAbierto = signal<boolean>(false);

  protected toggleMenu() {
    this.menuOpen.update((open) => !open);
    // El popup queda mal anclado al cambiar el ancho del drawer, así que se cierra.
    this.cerrarMenuPerfil();
  }

  protected alternarMenuPerfil(evento: Event) {
    // Sin esto el listener de document que cierra el popup lo volvería a cerrar
    // en el mismo clic que lo abre.
    evento.stopPropagation();
    const abierto = !this.menuPerfilAbierto();
    this.menuPerfilAbierto.set(abierto);
    if (!abierto) this.submenuIdiomaAbierto.set(false);
  }

  protected cerrarMenuPerfil() {
    this.menuPerfilAbierto.set(false);
    this.submenuIdiomaAbierto.set(false);
  }

  protected alternarSubmenuIdioma() {
    this.submenuIdiomaAbierto.update((abierto) => !abierto);
  }

  protected onResize() {
    const wasMobile = this.isMobile();
    const currentMobile = window.innerWidth <= 768;
    if (wasMobile !== currentMobile) {
      this.isMobile.set(currentMobile);
      this.menuOpen.set(false);
      this.cerrarMenuPerfil();
    }
  }

  protected isRail = computed<boolean>(() => !this.isMobile() && !this.menuOpen());

  protected logoUrl = computed<string>(() =>
    this.theme.isDarkMode() ? 'Assets/logos/myservy_blanco.png' : 'Assets/logos/myservy_negro.png'
  );
  private avatarGenerico = computed<string>(() =>
    this.theme.isDarkMode() ? 'Assets/images/Generic_avatar_DT.png' : 'Assets/images/Generic_avatar_LT.png'
  );

  /**
   * Foto del usuario en sesión. Antes era siempre el avatar genérico: la foto
   * nunca se leía, aunque la persona la tuviera cargada.
   *
   * Se prueba primero la sesión, que es el único origen que tiene un técnico:
   * su rol no puede leer el listado de miembros.
   */
  protected avatarUrl = computed<string>(() => {
    const usuario = this.auth.user();
    const delListado = usuario?.id ? this.usuariosService.getById(usuario.id) : undefined;
    const foto = usuario?.profile?.profilePhotoUrl ?? delListado?.profile?.profilePhotoUrl;
    return urlDeImagenDePerfil(foto) || this.avatarGenerico();
  });

  private usuariosService = inject(UsuariosService);

  private workspaceAccess = inject(WorkspaceAccessService);

  private dialog = inject(MatDialog);
  private soluciones = inject(SolucionesService);
  private solutionToken = inject(SolutionTokenService);
  protected userName = computed<string>(() => this.auth.user()?.fullName ?? '');
  /**
   * Rol del módulo de mantenimiento. Se toma del backend, que lo resuelve al
   * emitir el token: derivarlo del listado de usuarios fallaba porque un
   * técnico no tiene permiso para leerlo, y la caída mostraba el rol de la
   * plataforma ("Staff") en vez del suyo.
   */
  /**
   * Oculta del menú lo que el rol no puede abrir, según la regla de la matriz:
   * si no tiene acceso a una pantalla, tampoco ve su entrada. El backend igual
   * lo rechaza; esto evita ofrecer caminos que terminan en 403.
   */
  public puede(capacidad: string): boolean {
    return this.solutionToken.puede(capacidad);
  }

  /**
   * El rol se traduce por su clave (ADMINISTRATOR, TECHNICIAN...). Antes salía
   * de un mapa escrito a mano en español, así que la jerarquía se mostraba
   * siempre en español por más que la aplicación estuviera en otro idioma.
   */
  protected userRole = computed<string>(() => {
    // Se relee el idioma para que la etiqueta cambie al cambiarlo.
    this.langChangeSignal();

    const rolDeSesion = this.solutionToken.rol();
    if (rolDeSesion) return this.translate.instant(rolDeSesion);

    // Respaldo para cuando el token todavía no se emitió.
    const userId = this.auth.user()?.id;
    const u = userId ? this.usuariosService.getById(userId) : undefined;
    const roleRaw = u?.maintenanceRole || u?.role?.name;
    return roleRaw ? this.translate.instant(roleRaw) : '';
  });
  protected hasMultipleWorkspaces = computed<boolean>(() => {
    const workspaces = this.auth.user()?.workspaces;
    return workspaces != null && workspaces.length > 1;
  });

  /** Nombre del workspace en el que está trabajando, no el primero de la lista. */
  public readonly nombreWorkspaceActivo = computed(() => {
    const activo = this.auth.user()?.workspaceId;
    const ws = this.auth.user()?.workspaces?.find(w => w.id === activo);
    return ws?.name ?? $localize`Sin workspace`;
  });

  public async abrirCambioDeWorkspace(): Promise<void> {
    this.cerrarMenuPerfil();

    // El pedido arranca junto con el dialogo, no antes. Esperarlo aca dejaba
    // al usuario mirando la pantalla quieta despues del clic, sin saber si
    // habia pasado algo; el modal muestra su propio cargando mientras llega.
    const accesibles = this.workspaceAccess.resolverAccesibles(this.auth.user()?.workspaces ?? []);

    const ref = this.dialog.open(SwitchWorkspaceModal, {
      panelClass: 'switch-workspace-dialog',
      data: { workspaces: accesibles, activoId: this.auth.user()?.workspaceId ?? null },
      autoFocus: false,
    });

    const elegido = await firstValueFrom(ref.afterClosed());
    if (elegido) {
      // Las soluciones habilitadas son propias de cada workspace.
      this.soluciones.limpiar();
      await this.auth.switchWorkspace(elegido.id);
    }
  }

  /** Evita abrir dos modales si se vuelve a tocar mientras se piden las soluciones. */
  private pidiendoSoluciones = false;

  /**
   * Cambio de solucion. El listado sale de Console, que es quien sabe cuales
   * tiene habilitadas el workspace, y el destino se abre con el workspace
   * activo en la URL: la sesion ya viaja en la cookie del dominio, asi que del
   * otro lado se entra sin volver a iniciar sesion.
   */
  public async abrirCambioDeSolucion(): Promise<void> {
    if (this.pidiendoSoluciones) return;
    this.cerrarMenuPerfil();

    // En movil el drawer tapa el dialogo: se pliega antes de abrirlo.
    if (this.isMobile()) this.menuOpen.set(false);

    // El pedido arranca junto con el dialogo, no antes. Esperarlo aca dejaba
    // al usuario mirando la pantalla quieta despues del clic, sin saber si
    // habia pasado algo; el modal muestra su propio cargando mientras llega.
    const soluciones = this.soluciones.obtener(this.auth.workspaceId());

    this.pidiendoSoluciones = true;
    const ref = this.dialog.open(SwitchSolutionModal, {
      panelClass: 'switch-solution-dialog',
      data: { soluciones },
      autoFocus: false,
    });

    let elegida;
    try {
      elegida = await firstValueFrom(ref.afterClosed());
    } finally {
      this.pidiendoSoluciones = false;
    }
    if (!elegida?.url) return;

    window.location.assign(this.soluciones.conWorkspaceId(elegida.url, this.auth.workspaceId()));
  }

  protected cerrarSesion(): void {
    this.cerrarMenuPerfil();
    this.auth.logout();
  }
}
